import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Fade,
  InputAdornment,
  CircularProgress,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import Logo from "../components/Logo";
import { useMainStore } from "../store/store";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import TotpCodeField from "../components/TotpCodeField";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { login, verifyTwoFactor, error, loading } = useMainStore();
  const isPreview = process.env.REACT_APP_UI_PREVIEW === "true";
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });

  /** "password" is the first step, "code" is the two-factor confirmation. */
  const [step, setStep] = useState<"password" | "code">("password");
  const [code, setCode] = useState("");
  /**
   * The second-step cookie lives 5 minutes. A countdown keeps an expired
   * session from looking like "the code just won't work".
   */
  const [secondsLeft, setSecondsLeft] = useState(0);

  /**
   * Preview normally skips the sign-in form. `?preview_login=1` keeps it on
   * screen — otherwise neither login step can be viewed at all.
   */
  const previewLogin =
    new URLSearchParams(location.search).get("preview_login") === "1";

  useEffect(() => {
    if (isPreview && !previewLogin) {
      navigate("/transactions", { replace: true });
    }
  }, [isPreview, previewLogin, navigate]);

  useEffect(() => {
    if (step !== "code" || secondsLeft <= 0) return;
    const id = window.setInterval(
      () => setSecondsLeft((value) => Math.max(0, value - 1)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [step, secondsLeft]);

  const goToApp = () => {
    const from = location.state?.from;
    const redirectPath = !from || from === "/" ? "/transactions" : from;
    navigate(redirectPath, { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = await login(form.username, form.password);
    if (result.twofaRequired) {
      setStep("code");
      setCode("");
      setSecondsLeft(300);
      return;
    }
    if (result.ok) goToApp();
  };

  const handleVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = await verifyTwoFactor(code);
    if (result.ok) goToApp();
  };

  const backToPassword = () => {
    setStep("password");
    setCode("");
    setForm((current) => ({ ...current, password: "" }));
  };

  const expired = step === "code" && secondsLeft === 0;

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        position: "relative",
        p: { xs: 2, sm: 3 },
        background: isDark
          ? "radial-gradient(circle at 12% 12%, rgba(99,91,255,.30), transparent 30%), radial-gradient(circle at 88% 78%, rgba(22,184,196,.14), transparent 25%), #080d1b"
          : "radial-gradient(circle at 12% 12%, rgba(99,91,255,.20), transparent 30%), radial-gradient(circle at 88% 78%, rgba(22,184,196,.13), transparent 25%), #f4f6fb",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.18 : 0.3,
          backgroundImage:
            "linear-gradient(rgba(99,91,255,.14) 1px, transparent 1px), linear-gradient(90deg, rgba(99,91,255,.14) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "linear-gradient(to bottom right, black, transparent 70%)",
        }}
      />

      <Fade in timeout={600}>
        <Paper
          sx={{
            width: "100%",
            maxWidth: 1040,
            minHeight: { md: 620 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.08fr .92fr" },
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            borderRadius: { xs: "24px", md: "30px" },
            boxShadow: isDark
              ? "0 36px 90px rgba(0,0,0,.46)"
              : "0 36px 90px rgba(36,46,80,.18)",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
              overflow: "hidden",
              p: 6,
              color: "#fff",
              background:
                "linear-gradient(145deg, #7770ff 0%, #5a52ea 50%, #3831b5 100%)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 420,
                height: 420,
                borderRadius: "50%",
                right: -170,
                top: -120,
                border: "80px solid rgba(255,255,255,.08)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 270,
                height: 270,
                borderRadius: "50%",
                left: -100,
                bottom: -110,
                background: "rgba(22,184,196,.23)",
                filter: "blur(2px)",
              }}
            />

            <Stack direction="row" alignItems="center" spacing={1.5} zIndex={1}>
              <Box sx={{ display: "grid", placeItems: "center", borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,.22)" }}>
                <Logo size={44} />
              </Box>
              <Typography
                fontSize={28}
                fontWeight={800}
                letterSpacing="-.045em"
              >
                Finzora
              </Typography>
            </Stack>

            <Box sx={{ maxWidth: 390, zIndex: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  color: "#fff",
                  fontSize: { md: 46, lg: 52 },
                  lineHeight: 1.06,
                  mb: 2.5,
                }}
              >
                Payments under control
              </Typography>
              
            </Box>

            <Typography sx={{ color: "rgba(255,255,255,.58)", fontSize: 13 }}>
              Finzora merchant cabinet
            </Typography>
          </Box>

          <Box
            sx={{
              p: { xs: 3, sm: 6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              backgroundColor: "background.paper",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ display: { xs: "flex", md: "none" }, mb: 5 }}
            >
              <Box sx={{ display: "grid", placeItems: "center", borderRadius: "13px", overflow: "hidden" }}>
                <Logo size={40} />
              </Box>
              <Typography fontSize={25} fontWeight={800} letterSpacing="-.04em">
                Finzora
              </Typography>
            </Stack>

            <Typography variant="h4" sx={{ mb: 1 }}>
              {step === "password" ? "Welcome back" : "Confirm sign-in"}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {step === "password"
                ? "Enter your credentials to open the merchant cabinet."
                : `Open your authenticator app and enter the code for ${form.username}.`}
            </Typography>

            {step === "code" ? (
              <Box
                component="form"
                onSubmit={handleVerify}
                sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              >
                <TotpCodeField
                  value={code}
                  onChange={setCode}
                  autoFocus
                  disabled={loading || expired}
                  error={expired}
                  helperText={
                    expired
                      ? "Confirmation window expired — sign in again"
                      : `${Math.floor(secondsLeft / 60)}:${String(
                          secondsLeft % 60,
                        ).padStart(2, "0")} left`
                  }
                />

                {error && (
                  <Typography color="error.main" fontSize={14} fontWeight={600}>
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={code.length < 6 || loading || expired}
                  fullWidth
                  endIcon={!loading ? <ArrowForwardRounded /> : undefined}
                  sx={{ height: 50 }}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Confirm"
                  )}
                </Button>

                <Button variant="text" onClick={backToPassword} disabled={loading}>
                  Sign in with a different account
                </Button>
              </Box>
            ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              autoComplete="off"
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              <TextField
                label="Username"
                value={form.username}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    username: event.target.value,
                  }))
                }
                fullWidth
                required
                autoComplete="username"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineRounded color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
                fullWidth
                required
                autoComplete="current-password"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Show or hide password"
                          onClick={() => setShowPassword((value) => !value)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {error && (
                <Typography color="error.main" fontSize={14} fontWeight={600}>
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={!form.username || !form.password || loading}
                fullWidth
                endIcon={!loading ? <ArrowForwardRounded /> : undefined}
                sx={{ height: 50, mt: 0.5 }}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Sign in"
                )}
              </Button>
            </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default LoginPage;
